import jwtDecode from 'jwt-decode'

import authService from '~/services/auth.service'
import { UserRole } from '~/types/user-info-types'

/**
 * Who is signed in on this device, for the guards on the school screens.
 *
 * ⚠ The role is read from an *unverified* token, and only ever to pick a screen — hom-api does the
 * real enforcing. `authService.isAuthenticated()` alone answers "somebody is signed in", and in the
 * b2s edition that somebody can be a pupil or a staff account; this is about not rendering a panel
 * whose every request would 403, and not sending staff to a pupil's dashboard.
 */
export const schoolSession = {
  /**
   * `null` when nobody is signed in **and** when the token cannot be decoded, so it does not answer
   * "is anybody signed in" — `authService.isAuthenticated()` does. Callers that branch on both must
   * ask that first, or a malformed token reads as signed out.
   */
  readRole: (): string | null => {
    const token = authService.getAccessToken()

    if (!token) return null

    try {
      return jwtDecode<{ role?: string }>(token).role ?? null
    } catch {
      // A malformed token is not a role — throwing would blank the screen instead of redirecting.
      return null
    }
  },

  /** Built on `readRole` so the two can never disagree about who a teacher is. */
  isTeacherSignedIn: (): boolean => schoolSession.readRole() === UserRole.SCHOOL_TEACHER,
}
